import { CommonModule } from '@angular/common';
import { Component, NgZone, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { VW_ERROR_CODES } from 'vwappservices';
import { SettingsApiService } from '../../services/settings-api.service';
import { notifyResult, reportApiError } from '../../shared/api-result.helper';
import { ConfirmService } from '../../shared/confirm.service';

interface GenericDowntimeReason {
  _id: string;
  name: string;
}

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnInit {
  settings = { globalTrackingMode: 'checkCount', shiftStart: '09:00', shiftEnd: '18:00', behindTargetAlertThresholdPct: 15 };
  loading = true;

  // Tracking Mode and Shift & Alerts save independently (separate cards, separate buttons), so
  // each needs its own saving flag and its own dirty check against the last-loaded/saved snapshot
  // — editing one card's fields shouldn't enable the other card's Save button.
  savingTrackingMode = false;
  savingShiftAlerts = false;

  private savedSettings = { ...this.settings };

  get isTrackingModeDirty(): boolean {
    return this.settings.globalTrackingMode !== this.savedSettings.globalTrackingMode;
  }

  get isShiftAlertsDirty(): boolean {
    return (
      this.settings.shiftStart !== this.savedSettings.shiftStart ||
      this.settings.shiftEnd !== this.savedSettings.shiftEnd ||
      this.settings.behindTargetAlertThresholdPct !== this.savedSettings.behindTargetAlertThresholdPct
    );
  }

  // Applies to every step's downtime form regardless of machine type (e.g. "Power Outage") —
  // managed separately from the main Save Settings form since each add/remove hits the API
  // immediately (same inline-list pattern as a Machine Type's own downtimeReasons).
  genericDowntimeReasons: GenericDowntimeReason[] = [];
  newGenericReasonName = '';

  private toastr = inject(ToastrService);
  private confirmService = inject(ConfirmService);

  constructor(
    private settingsApi: SettingsApiService,
    private ngZone: NgZone,
  ) {}

  ngOnInit() {
    this.load();
  }

  async load() {
    this.loading = true;
    const res = await this.settingsApi.get();
    // The await continuation here can resume outside Angular's zone (same issue documented in
    // DashboardComponent.load()) — loading would flip to false internally but the view never gets
    // told to re-render, so the page stays stuck showing nothing until something unrelated forces
    // a repaint. ngZone.run() guarantees a change-detection tick regardless of what zone the
    // continuation actually resumed in.
    this.ngZone.run(() => {
      if (res.errorCode === VW_ERROR_CODES.SUCCESS && res.Data) {
        this.settings = {
          globalTrackingMode: res.Data.globalTrackingMode,
          shiftStart: res.Data.shiftStart,
          shiftEnd: res.Data.shiftEnd,
          behindTargetAlertThresholdPct: res.Data.behindTargetAlertThresholdPct,
        };
        this.genericDowntimeReasons = res.Data.genericDowntimeReasons || [];
        this.savedSettings = { ...this.settings };
      } else {
        reportApiError(this.toastr, res);
      }
      this.loading = false;
    });
  }

  async saveTrackingMode() {
    this.savingTrackingMode = true;
    const res = await this.settingsApi.update({ globalTrackingMode: this.settings.globalTrackingMode });
    this.savingTrackingMode = false;
    if (notifyResult(this.toastr, res, 'Tracking mode saved.')) {
      this.savedSettings = { ...this.savedSettings, globalTrackingMode: this.settings.globalTrackingMode };
    }
  }

  async saveShiftAlerts() {
    this.savingShiftAlerts = true;
    const res = await this.settingsApi.update({
      shiftStart: this.settings.shiftStart,
      shiftEnd: this.settings.shiftEnd,
      behindTargetAlertThresholdPct: this.settings.behindTargetAlertThresholdPct,
    });
    this.savingShiftAlerts = false;
    if (notifyResult(this.toastr, res, 'Shift & alert settings saved.')) {
      this.savedSettings = {
        ...this.savedSettings,
        shiftStart: this.settings.shiftStart,
        shiftEnd: this.settings.shiftEnd,
        behindTargetAlertThresholdPct: this.settings.behindTargetAlertThresholdPct,
      };
    }
  }

  async addGenericReason() {
    const name = this.newGenericReasonName.trim();
    if (!name) return;
    const res = await this.settingsApi.addGenericDowntimeReason(name);
    if (!notifyResult(this.toastr, res, `Reason "${name}" added.`)) return;
    this.newGenericReasonName = '';
    await this.load();
  }

  async deleteGenericReason(reason: GenericDowntimeReason) {
    const ok = await this.confirmService.confirm({
      message: `Delete downtime reason "${reason.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!ok) return;
    const res = await this.settingsApi.deleteGenericDowntimeReason(reason._id);
    if (!notifyResult(this.toastr, res, `${reason.name} removed.`)) return;
    await this.load();
  }
}
